import { useState } from 'react';
import styled from 'styled-components';
import colors from '../../../../styles/colors';
import { apiAdminCreateQuiz, apiAdminUpdateQuiz } from '../../../../api/quizApi';
import type { AdminQuiz } from '../../../../types/models/quiz';

interface ChoiceDraft {
  text: string;
  is_correct: boolean;
}

interface QuizCreateModalProps {
  quiz?: AdminQuiz;
  onClose: () => void;
  onSaved: (quiz: AdminQuiz) => void;
}

// ─── Styled ───────────────────────────────────────────────────────────────────

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background-color: rgba(0,0,0,0.65);
  z-index: 1000;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 40px 16px;
  overflow-y: auto;
`;

const Dialog = styled.div`
  width: 100%;
  max-width: 580px;
  background-color: ${colors.bgColorBlack2};
  border: 1px solid rgba(255,215,0,0.3);
  border-radius: 8px;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid rgba(255,255,255,0.08);
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: gold;
`;

const CloseBtn = styled.button`
  background: none;
  border: none;
  color: rgba(255,255,255,0.5);
  font-size: 20px;
  cursor: pointer;
  padding: 0 4px;
  line-height: 1;
  &:hover { color: #fff; }
`;

const ModalBody = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 20px;
  border-top: 1px solid rgba(255,255,255,0.08);
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 13px;
  font-weight: 500;
  color: rgba(255,255,255,0.7);
`;

const Textarea = styled.textarea`
  padding: 8px 10px;
  border-radius: 4px;
  border: 1px solid rgba(255,255,255,0.15);
  background-color: rgba(0,0,0,0.3);
  color: #fff;
  font-size: 14px;
  width: 100%;
  box-sizing: border-box;
  resize: vertical;
  min-height: 80px;
  &:focus { outline: none; border-color: gold; }
  &:disabled { opacity: 0.5; }
`;

const Input = styled.input`
  padding: 8px 10px;
  border-radius: 4px;
  border: 1px solid rgba(255,255,255,0.15);
  background-color: rgba(0,0,0,0.3);
  color: #fff;
  font-size: 14px;
  flex: 1;
  &:focus { outline: none; border-color: gold; }
  &:disabled { opacity: 0.5; }
`;

const ChoicesSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ChoiceRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: rgba(255,255,255,0.5);
  white-space: nowrap;
  cursor: pointer;

  input[type="radio"] {
    accent-color: gold;
    cursor: pointer;
  }
`;

const RemoveBtn = styled.button`
  background: none;
  border: 1px solid rgba(248,113,113,0.3);
  border-radius: 4px;
  color: #f87171;
  font-size: 12px;
  padding: 4px 8px;
  cursor: pointer;
  flex-shrink: 0;
  &:hover { background-color: rgba(248,113,113,0.1); }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const AddChoiceBtn = styled.button`
  align-self: flex-start;
  background: none;
  border: 1px dashed rgba(255,255,255,0.2);
  border-radius: 4px;
  color: rgba(255,255,255,0.5);
  font-size: 12px;
  padding: 5px 12px;
  cursor: pointer;
  &:hover { border-color: rgba(255,255,255,0.4); color: rgba(255,255,255,0.7); }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CheckLabel = styled.label`
  font-size: 13px;
  color: rgba(255,255,255,0.7);
  cursor: pointer;
`;

const ErrorMsg = styled.p`
  margin: 0;
  color: #f87171;
  font-size: 12px;
`;

const GeneralError = styled.p`
  margin: 0;
  padding: 8px 12px;
  background-color: rgba(248,113,113,0.1);
  border: 1px solid rgba(248,113,113,0.3);
  border-radius: 4px;
  color: #f87171;
  font-size: 13px;
`;

const BtnSecondary = styled.button`
  padding: 8px 18px;
  border-radius: 4px;
  border: 1px solid rgba(255,255,255,0.2);
  background: transparent;
  color: rgba(255,255,255,0.7);
  font-size: 14px;
  cursor: pointer;
  &:hover { background-color: rgba(255,255,255,0.05); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const BtnPrimary = styled.button`
  padding: 8px 22px;
  border-radius: 4px;
  border: none;
  background-color: gold;
  color: #000;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  &:hover:not(:disabled) { background-color: #e6c200; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const HintText = styled.p`
  font-size: 11px;
  color: rgba(255,255,255,0.3);
  margin: 0;
`;

// ─── Component ────────────────────────────────────────────────────────────────

const QuizCreateModal: React.FC<QuizCreateModalProps> = ({ quiz, onClose, onSaved }) => {
  const isEdit = !!quiz;

  const [question, setQuestion] = useState(quiz?.question ?? '');
  const [isActive, setIsActive] = useState(quiz?.is_active ?? true);
  const [isPriority, setIsPriority] = useState(quiz?.is_priority ?? false);
  const [choices, setChoices] = useState<ChoiceDraft[]>(
    quiz?.choices.map((c) => ({ text: c.text, is_correct: c.is_correct })) ??
    [{ text: '', is_correct: true }, { text: '', is_correct: false }]
  );
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const setCorrect = (idx: number) => {
    setChoices((prev) => prev.map((c, i) => ({ ...c, is_correct: i === idx })));
  };

  const updateChoiceText = (idx: number, text: string) => {
    setChoices((prev) => prev.map((c, i) => (i === idx ? { ...c, text } : c)));
  };

  const addChoice = () => {
    if (choices.length >= 6) return;
    setChoices((prev) => [...prev, { text: '', is_correct: false }]);
  };

  const removeChoice = (idx: number) => {
    if (choices.length <= 2) return;
    const next = choices.filter((_, i) => i !== idx);
    // se rimosso era correct, assegna il primo
    if (choices[idx].is_correct) {
      next[0].is_correct = true;
    }
    setChoices(next);
  };

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!question.trim()) errs.question = 'La domanda è obbligatoria.';
    if (choices.some((c) => !c.text.trim())) errs.choices = 'Tutte le risposte devono avere un testo.';
    if (!choices.some((c) => c.is_correct)) errs.choices = 'Seleziona la risposta corretta.';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setIsSaving(true);
    setErrors({});
    const body = { question: question.trim(), is_active: isActive, is_priority: isPriority, choices };
    try {
      const result = isEdit
        ? await apiAdminUpdateQuiz(quiz.id, body)
        : await apiAdminCreateQuiz(body);
      onSaved(result);
    } catch (err: any) {
      const serverErrors = err?.response?.data?.errors ?? err?.errors;
      if (serverErrors) setErrors(serverErrors);
      else setErrors({ general: 'Si è verificato un errore. Riprova.' });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Overlay onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <Dialog>
        <ModalHeader>
          <ModalTitle>{isEdit ? `Modifica Domanda #${quiz.id}` : 'Nuova Domanda'}</ModalTitle>
          <CloseBtn type="button" onClick={onClose}>✕</CloseBtn>
        </ModalHeader>

        <ModalBody>
          {errors.general && <GeneralError>{errors.general}</GeneralError>}

          <FormGroup>
            <Label>Domanda</Label>
            <Textarea
              placeholder="es: Chi ha vinto il primo Royal Rumble?"
              value={question}
              disabled={isSaving}
              onChange={(e) => setQuestion(e.target.value)}
            />
            {errors.question && <ErrorMsg>{errors.question}</ErrorMsg>}
          </FormGroup>

          <FormGroup>
            <Label>Risposte (2–6) — seleziona quella corretta</Label>
            <ChoicesSection>
              {choices.map((c, i) => (
                <ChoiceRow key={i}>
                  <RadioLabel>
                    <input
                      type="radio"
                      name="correct_choice"
                      checked={c.is_correct}
                      disabled={isSaving}
                      onChange={() => setCorrect(i)}
                    />
                    Corretta
                  </RadioLabel>
                  <Input
                    type="text"
                    placeholder={`Risposta ${i + 1}`}
                    value={c.text}
                    disabled={isSaving}
                    onChange={(e) => updateChoiceText(i, e.target.value)}
                  />
                  <RemoveBtn
                    type="button"
                    disabled={isSaving || choices.length <= 2}
                    onClick={() => removeChoice(i)}
                  >
                    ✕
                  </RemoveBtn>
                </ChoiceRow>
              ))}
              <AddChoiceBtn
                type="button"
                disabled={isSaving || choices.length >= 6}
                onClick={addChoice}
              >
                + Aggiungi risposta
              </AddChoiceBtn>
            </ChoicesSection>
            {errors.choices && <ErrorMsg>{errors.choices}</ErrorMsg>}
            <HintText>Il radio button indica la risposta corretta.</HintText>
          </FormGroup>

          <FormGroup>
            <ToggleRow>
              <input
                type="checkbox"
                id="is_active"
                checked={isActive}
                disabled={isSaving}
                onChange={(e) => setIsActive(e.target.checked)}
              />
              <CheckLabel htmlFor="is_active">Attiva (la domanda entra nel pool random)</CheckLabel>
            </ToggleRow>
          </FormGroup>

          <FormGroup>
            <ToggleRow>
              <input
                type="checkbox"
                id="is_priority"
                checked={isPriority}
                disabled={isSaving}
                onChange={(e) => setIsPriority(e.target.checked)}
              />
              <CheckLabel htmlFor="is_priority">
                Priorità — mostrata al primo slot disponibile di ogni utente (non riproposta)
              </CheckLabel>
            </ToggleRow>
          </FormGroup>
        </ModalBody>

        <ModalFooter>
          <BtnSecondary type="button" disabled={isSaving} onClick={onClose}>Annulla</BtnSecondary>
          <BtnPrimary type="button" disabled={isSaving} onClick={handleSubmit}>
            {isSaving ? 'Salvataggio...' : isEdit ? 'Salva Modifiche' : 'Salva'}
          </BtnPrimary>
        </ModalFooter>
      </Dialog>
    </Overlay>
  );
};

export default QuizCreateModal;
